/**
 * MensagemChat - Componente individual de mensagem no chat
 *
 * CONTEXTO DE NEGÓCIO:
 * Representa uma única mensagem na conversa entre o usuário e a IA.
 * Exibe o conteúdo da mensagem com estilo diferenciado para mensagens
 * do usuário versus mensagens da IA (assistente).
 *
 * RESPONSABILIDADES:
 * - Renderizar o conteúdo da mensagem (texto)
 * - Aplicar estilo visual diferente para usuário e IA
 * - Exibir timestamp da mensagem
 * - Dar feedback visual de mensagem em processamento (loading)
 *
 * IMPLEMENTAÇÃO:
 * - Componente puro (sem estado interno)
 * - Usa Tailwind CSS para estilização
 * - Mensagens do usuário: alinhadas à direita, fundo azul
 * - Mensagens da IA: alinhadas à esquerda, fundo cinza
 * - Suporte para estado "carregando" (typing indicator)
 *
 * NOTA PARA LLMs:
 * Este componente faz parte da TAREFA-091 - Frontend Componente de Chat.
 * É usado pelo ComponenteChatIA para renderizar cada mensagem individual.
 */

import { useMemo } from 'react';
import { Bot, User, Loader2 } from 'lucide-react';

// ===== TIPOS =====

/**
 * Autor de uma mensagem
 * - usuario: Mensagem enviada pelo advogado/usuário
 * - ia: Resposta gerada pela inteligência artificial
 */
export type AutorMensagem = 'usuario' | 'ia';

/**
 * Interface de uma mensagem no chat
 */
export interface MensagemChatInterface {
  /** Identificador único da mensagem (UUID ou timestamp) */
  id: string;

  /** Conteúdo textual da mensagem */
  conteudo: string;

  /** Autor da mensagem (usuário ou IA) */
  autor: AutorMensagem;

  /** Timestamp de quando a mensagem foi criada (ISO 8601) */
  timestamp: string;

  /** Indica se a mensagem ainda está sendo processada/gerada */
  carregando?: boolean;
}

/**
 * Props do componente MensagemChat
 */
interface MensagemChatProps {
  /** Dados da mensagem a ser exibida */
  mensagem: MensagemChatInterface;

  /** Classe CSS adicional para customização (opcional) */
  className?: string;
}

// ===== COMPONENTE =====

/**
 * Componente de exibição de mensagem individual no chat
 *
 * @param props - Propriedades do componente
 * @returns Elemento JSX da mensagem
 */
export function MensagemChat({ mensagem, className = '' }: MensagemChatProps): JSX.Element {
  // Determinar se a mensagem é do usuário ou da IA
  const ehMensagemDoUsuario = mensagem.autor === 'usuario';

  // Formatar timestamp para exibição (ex: "14:30")
  const timestampFormatado = useMemo(() => {
    try {
      const data = new Date(mensagem.timestamp);
      return data.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  }, [mensagem.timestamp]);

  // Classes CSS dinâmicas baseadas no autor da mensagem
  const classeContainerMensagem = ehMensagemDoUsuario
    ? 'flex justify-end mb-4'
    : 'flex justify-start mb-4';

  const classeBaloMensagem = ehMensagemDoUsuario
    ? 'bg-blue-600 text-white rounded-2xl rounded-tr-sm px-4 py-3 max-w-[75%] shadow-sm'
    : 'bg-gray-100 text-gray-800 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[75%] shadow-sm';

  const IconeAutor = ehMensagemDoUsuario ? User : Bot;
  const classeIcone = ehMensagemDoUsuario
    ? 'text-blue-600'
    : 'text-gray-600';

  return (
    <div className={`${classeContainerMensagem} ${className}`}>
      {/* Avatar/Ícone do autor (só para IA) */}
      {!ehMensagemDoUsuario && (
        <div className="mr-2 shrink-0">
          <div className="flex size-8 items-center justify-center rounded-full bg-gray-200">
            <IconeAutor className={`size-5 ${classeIcone}`} />
          </div>
        </div>
      )}

      {/* Conteúdo da mensagem */}
      <div className="flex flex-col">
        <div className={classeBaloMensagem}>
          {/* Indicador de carregamento (typing) */}
          {mensagem.carregando ? (
            <div className="flex items-center space-x-2">
              <Loader2 className="size-4 animate-spin" />
              <span className="text-sm">A IA está digitando...</span>
            </div>
          ) : (
            <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
              {mensagem.conteudo}
            </p>
          )}
        </div>

        {/* Timestamp */}
        {!mensagem.carregando && timestampFormatado && (
          <span
            className={`mt-1 text-xs text-gray-500 ${
              ehMensagemDoUsuario ? 'text-right' : 'ml-10 text-left'
            }`}
          >
            {timestampFormatado}
          </span>
        )}
      </div>

      {/* Avatar/Ícone do autor (só para usuário) */}
      {ehMensagemDoUsuario && (
        <div className="ml-2 shrink-0">
          <div className="flex size-8 items-center justify-center rounded-full bg-blue-100">
            <IconeAutor className={`size-5 ${classeIcone}`} />
          </div>
        </div>
      )}
    </div>
  );
}
